import React from "react";
import { Link } from "react-router-dom";
import { LOGO_ICON, SITE_NAME, TAGLINE, PUBLISHER, SUPPORT_EMAIL, NAV_LINKS } from "@/content/siteConfig";
import { GooglePlayBadge } from "@/components/brand/GooglePlayBadge";
import { getPostBySlug } from "@/content/blogPosts";

const footerReadSlugs = [
  "who-crenelview-is-built-for",
  "block-apps-child-uses-my-phone",
  "screen-pinning-vs-app-blocker-shared-phone",
];

export const Footer: React.FC = () => {
  const year = new Date().getFullYear();
  const readPosts = footerReadSlugs.map((slug) => getPostBySlug(slug)).filter(Boolean);

  return (
    <footer className="border-t border-cvborder bg-ink text-canvas" data-testid="site-footer">
      <div className="mx-auto max-w-6xl px-6 py-14 sm:py-16">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-[1.2fr,0.8fr,1fr,1fr]">
          <div>
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo-link">
              <img src={LOGO_ICON} alt={SITE_NAME} width={36} height={36} className="h-9 w-9 rounded-[10px]" />
              <span className="font-heading text-xl text-canvas">{SITE_NAME}</span>
            </Link>
            <p className="mt-4 max-w-xs text-sm text-canvas/60">{TAGLINE}</p>
            <p className="mt-3 max-w-xs text-sm text-canvas/40">
              For one Android phone shared between a parent and a young child. No account, no server, no data leaving the device.
            </p>
            <GooglePlayBadge data-testid="footer-play-cta" className="mt-5 h-11" />
          </div>

          <div>
            <p className="text-sm font-semibold text-canvas/50">Site</p>
            <ul className="mt-4 flex flex-col gap-3">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm text-canvas/80 hover:text-canvas" data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-sm font-semibold text-canvas/50">From the blog</p>
            <ul className="mt-4 flex flex-col gap-3">
              {readPosts.map((post) => (
                <li key={post!.slug}>
                  <Link
                    to={`/blog/${post!.slug}`}
                    className="text-sm text-canvas/80 hover:text-canvas"
                    data-testid={`footer-blog-link-${post!.slug}`}
                  >
                    {post!.title}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/blog" className="text-sm font-semibold text-canvas hover:text-canvas/80" data-testid="footer-blog-all-link">
                  All articles -&gt;
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-sm font-semibold text-canvas/50">Legal</p>
            <ul className="mt-4 flex flex-col gap-3">
              <li>
                <Link to="/privacy" className="text-sm text-canvas/80 hover:text-canvas" data-testid="footer-link-privacy-claims">
                  Privacy claims
                </Link>
              </li>
              <li>
                <Link to="/privacy-policy" className="text-sm text-canvas/80 hover:text-canvas" data-testid="footer-link-privacy-policy">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm text-canvas/80 hover:text-canvas" data-testid="footer-support-email">
                  {SUPPORT_EMAIL}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-canvas/10 pt-6 text-xs text-canvas/50 sm:flex-row sm:items-center sm:justify-between">
          <p>&copy; {year} {PUBLISHER}. All rights reserved.</p>
          <p>No account. No server. No tracking. Android only.</p>
        </div>
      </div>
    </footer>
  );
};
